import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState, AppDispatch } from "../../store/store";
import { Typography } from "@material-tailwind/react";
import moment from "moment";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import {
  fetchContactInquiries,
  toggleResolveContact,
  deleteContactInquiry,
  exportAllContactInquiries,
} from "../../store/ContactSlice";
import { ContactInquiry } from "../../interfaces/Contact";
import Modal from "../Modal";

const ContactListView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [isExporting, setIsExporting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [selectedInquiry, setSelectedInquiry] = useState<ContactInquiry | null>(null);

  const { data, loading } = useSelector((state: RootState) => state.contact);

  const TABLE_HEAD = [
    "#",
    t("adminContacts.table.contact", "NAME & EMAIL"),
    t("adminContacts.table.subject", "SUBJECT"),
    t("adminContacts.table.message", "MESSAGE"),
    t("adminContacts.table.date", "DATE RECEIVED"),
    t("adminContacts.table.status", "STATUS"),
    t("adminContacts.table.actions", "ACTIONS"),
  ];

  const debouncedSearchHandler = useRef(
    debounce((query: string) => {
      setDebouncedSearch(query);
      setPage(1);
    }, 400)
  ).current;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);
    debouncedSearchHandler(val);
  };

  const handleClearSearch = () => {
    setSearch("");
    setDebouncedSearch("");
    setPage(1);
  };

  const handleStatusChange = (status: string) => {
    setStatusFilter(status);
    setPage(1);
  };

  useEffect(() => {
    dispatch(
      fetchContactInquiries({
        page,
        limit,
        search: debouncedSearch,
        status: statusFilter,
      })
    );
  }, [dispatch, page, limit, debouncedSearch, statusFilter]);

  const handleToggleResolve = async (contact: ContactInquiry) => {
    setTogglingId(contact.id);
    try {
      await dispatch(
        toggleResolveContact({ id: contact.id, isResolved: !contact.isResolved })
      ).unwrap();
      toast.success(
        !contact.isResolved
          ? t("adminContacts.markedResolved", "Inquiry marked as resolved")
          : t("adminContacts.markedPending", "Inquiry marked as pending")
      );
    } catch (err: any) {
      toast.error(err || t("adminContacts.statusError", "Failed to update status"));
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (
      window.confirm(
        t(
          "adminContacts.confirmDelete",
          `Are you sure you want to delete the contact message from ${name}?`
        )
      )
    ) {
      setDeletingId(id);
      try {
        await dispatch(deleteContactInquiry(id)).unwrap();
        toast.success(
          t("adminContacts.deleteSuccess", "Contact inquiry deleted successfully")
        );
        if (selectedInquiry?.id === id) {
          setSelectedInquiry(null);
        }
      } catch (err: any) {
        toast.error(
          err || t("adminContacts.deleteError", "Failed to delete contact inquiry")
        );
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const result = await dispatch(exportAllContactInquiries()).unwrap();
      if (!result || result.length === 0) {
        toast.info(t("adminContacts.noInquiriesExport", "No inquiries to export"));
        setIsExporting(false);
        return;
      }

      const headers = [
        "ID",
        "Sender Name",
        "Sender Email",
        "Subject",
        "Message",
        "Status",
        "Date Received",
      ];
      const rows = result.map((c: ContactInquiry) => [
        c.id,
        `"${(c.name || "").replace(/"/g, '""')}"`,
        `"${(c.email || "").replace(/"/g, '""')}"`,
        `"${(c.subject || "General Inquiry").replace(/"/g, '""')}"`,
        `"${(c.description || "").replace(/"/g, '""')}"`,
        c.isResolved ? "Resolved" : "Pending",
        c.createdAt ? moment(c.createdAt).format("YYYY-MM-DD HH:mm:ss") : "",
      ]);

      const csvContent =
        "data:text/csv;charset=utf-8," +
        [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute(
        "download",
        `contact_inquiries_${moment().format("YYYY-MM-DD")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(
        t("adminContacts.exportSuccess", "Contact inquiries exported successfully!")
      );
    } catch (error: any) {
      toast.error(t("adminContacts.exportError", "Failed to export CSV"));
    } finally {
      setIsExporting(false);
    }
  };

  const contacts = data?.contacts || [];
  const total = data?.total || 0;
  const pendingCount = data?.pendingCount || 0;
  const resolvedCount = data?.resolvedCount || 0;
  const allCount = data?.allCount ?? (pendingCount + resolvedCount);
  const totalPages = data?.totalPages || 1;

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      {/* Toolbar & Filters */}
      <div className="p-4 bg-white border-b border-gray-100 flex flex-wrap items-center justify-between gap-4">
        {/* Search */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-64 sm:w-72">
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder={t(
                "adminContacts.searchPlaceholder",
                "Search by name, email, or content..."
              )}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B4804A]"
            />
            {search && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2.5 top-2 text-gray-400 hover:text-gray-600 text-sm cursor-pointer"
              >
                ✕
              </button>
            )}
          </div>

          {/* Status Tabs */}
          <div className="inline-flex bg-gray-100 p-1 rounded-lg text-xs font-semibold">
            <button
              onClick={() => handleStatusChange("")}
              className={`px-3 py-1.5 rounded-md transition-colors cursor-pointer ${
                statusFilter === ""
                  ? "bg-white text-[#041B44] shadow-xs"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {t("adminContacts.filterAll", "All")} ({allCount})
            </button>
            <button
              onClick={() => handleStatusChange("pending")}
              className={`px-3 py-1.5 rounded-md transition-colors cursor-pointer ${
                statusFilter === "pending"
                  ? "bg-amber-500 text-white shadow-xs"
                  : "text-gray-600 hover:text-amber-700"
              }`}
            >
              {t("adminContacts.filterPending", "Pending")} ({pendingCount})
            </button>
            <button
              onClick={() => handleStatusChange("resolved")}
              className={`px-3 py-1.5 rounded-md transition-colors cursor-pointer ${
                statusFilter === "resolved"
                  ? "bg-emerald-600 text-white shadow-xs"
                  : "text-gray-600 hover:text-emerald-700"
              }`}
            >
              {t("adminContacts.filterResolved", "Resolved")} ({resolvedCount})
            </button>
          </div>
        </div>

        {/* Export Button */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleExportCSV}
            disabled={isExporting || total === 0}
            className="flex items-center gap-2 bg-[#041B44] hover:bg-[#031535] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 cursor-pointer"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            {isExporting
              ? t("adminContacts.exporting", "Exporting...")
              : t("adminContacts.exportCSV", "Export to CSV")}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="w-full flex-1 overflow-x-auto overflow-y-auto">
        <table className="w-full min-w-[850px] table-auto text-left">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="bg-[#F8F9FA] text-left border-b-2 border-gray-100">
              {TABLE_HEAD.map((head, i) => (
                <th
                  key={i}
                  className="py-4 px-6 font-bold text-[#7E8299] uppercase text-xs whitespace-nowrap"
                >
                  <Typography
                    variant="small"
                    className="font-semibold leading-none text-[#7E8299] opacity-70 whitespace-nowrap"
                  >
                    {head}
                  </Typography>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={7} className="text-center py-12 text-gray-500 font-medium">
                  {t("common.loading", "Loading...")}
                </td>
              </tr>
            ) : contacts.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-12 text-gray-500 font-medium">
                  {t("adminContacts.noInquiries", "No contact inquiries found")}
                </td>
              </tr>
            ) : (
              contacts.map((contact, index) => (
                <tr
                  key={contact.id || index}
                  className="hover:bg-gray-50/50 transition-colors"
                >
                  {/* # */}
                  <td className="py-4 px-6 whitespace-nowrap text-sm text-gray-500 font-medium">
                    {(page - 1) * limit + index + 1}
                  </td>

                  {/* Name & Email */}
                  <td className="py-4 px-6 whitespace-nowrap">
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">
                        {contact.name}
                      </p>
                      <a
                        href={`mailto:${contact.email}`}
                        className="text-xs text-[#037897] hover:underline"
                      >
                        {contact.email}
                      </a>
                    </div>
                  </td>

                  {/* Subject */}
                  <td className="py-4 px-6 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-700">
                      {contact.subject || "General Inquiry"}
                    </span>
                  </td>

                  {/* Message Snippet */}
                  <td className="py-4 px-6 max-w-xs">
                    <div
                      onClick={() => setSelectedInquiry(contact)}
                      className="cursor-pointer group flex items-center gap-1.5"
                    >
                      <p className="text-sm text-gray-600 truncate group-hover:text-[#037897] transition-colors">
                        {contact.description}
                      </p>
                      <span className="text-xs text-[#037897] shrink-0 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        View
                      </span>
                    </div>
                  </td>

                  {/* Date */}
                  <td className="py-4 px-6 whitespace-nowrap text-sm text-gray-600">
                    {contact.createdAt
                      ? moment(contact.createdAt).format("DD MMM, YYYY HH:mm")
                      : "-"}
                  </td>

                  {/* Status Toggle */}
                  <td className="py-4 px-6 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleResolve(contact)}
                      disabled={togglingId === contact.id}
                      title={
                        contact.isResolved
                          ? "Click to mark as pending"
                          : "Click to mark as resolved"
                      }
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold cursor-pointer transition-colors ${
                        contact.isResolved
                          ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                          : "bg-amber-100 text-amber-800 hover:bg-amber-200"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          contact.isResolved ? "bg-emerald-600" : "bg-amber-500"
                        }`}
                      ></span>
                      <span>
                        {contact.isResolved
                          ? t("adminContacts.statusResolved", "Resolved")
                          : t("adminContacts.statusPending", "Pending")}
                      </span>
                    </button>
                  </td>

                  {/* Actions */}
                  <td className="py-4 px-6 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      {/* View Details */}
                      <button
                        onClick={() => setSelectedInquiry(contact)}
                        className="text-gray-500 hover:text-[#037897] transition-colors p-1"
                        title={t("adminContacts.viewDetails", "View Details")}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      </button>

                      {/* Reply via email */}
                      <a
                        href={`mailto:${contact.email}?subject=${encodeURIComponent(
                          `Re: ${contact.subject || "Contact Inquiry"} - Centre SEVA`
                        )}`}
                        className="text-[#037897] hover:text-[#025a72] transition-colors p-1"
                        title={t("adminContacts.replyEmail", "Reply via Email")}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                          />
                        </svg>
                      </a>

                      {/* Delete */}
                      <button
                        onClick={() => handleDelete(contact.id, contact.name)}
                        disabled={deletingId === contact.id}
                        className="text-red-500 hover:text-red-700 transition-colors p-1 cursor-pointer"
                        title={t("adminContacts.delete", "Delete")}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <div className="p-4 bg-white border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {t("adminContacts.page", "Page")} {page} / {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="px-3 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
            >
              {t("adminContacts.prev", "Previous")}
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className="px-3 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
            >
              {t("adminContacts.next", "Next")}
            </button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedInquiry && (
        <Modal
          isOpen={true}
          onClose={() => setSelectedInquiry(null)}
          title={selectedInquiry.subject || t("adminContacts.generalInquiry", "General Inquiry")}
          subtitle={t("adminContacts.modalTitle", "Contact Inquiry Details")}
          size="xl"
          footer={
            <>
              <button
                onClick={() => handleToggleResolve(selectedInquiry)}
                className={`w-full sm:w-auto px-4 py-2 rounded-lg text-xs font-bold transition-colors cursor-pointer text-center ${
                  selectedInquiry.isResolved
                    ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                    : "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                }`}
              >
                {selectedInquiry.isResolved
                  ? t("adminContacts.markAsPending", "Mark as Pending")
                  : t("adminContacts.markAsResolved", "Mark as Resolved")}
              </button>

              <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                <button
                  onClick={() => setSelectedInquiry(null)}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-xs font-bold transition-colors cursor-pointer text-center"
                >
                  {t("common.close", "Close")}
                </button>
                <a
                  href={`mailto:${selectedInquiry.email}?subject=${encodeURIComponent(
                    `Re: ${selectedInquiry.subject || "Contact Inquiry"} - Centre SEVA`
                  )}`}
                  className="bg-[#037897] hover:bg-[#025a72] text-white px-4 py-2 rounded-lg text-xs font-bold transition-colors inline-flex items-center justify-center gap-1.5"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                    />
                  </svg>
                  <span>{t("adminContacts.replyNow", "Reply via Email")}</span>
                </a>
              </div>
            </>
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 bg-gray-50 p-3 sm:p-4 rounded-xl border border-gray-100">
            <div>
              <p className="text-[11px] font-semibold text-gray-400 uppercase">
                {t("adminContacts.sender", "Sender")}
              </p>
              <p className="text-sm font-bold text-gray-900 mt-0.5 truncate">
                {selectedInquiry.name}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-gray-400 uppercase">
                {t("adminContacts.email", "Email")}
              </p>
              <a
                href={`mailto:${selectedInquiry.email}`}
                className="text-sm font-semibold text-[#037897] hover:underline mt-0.5 block truncate"
              >
                {selectedInquiry.email}
              </a>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-gray-400 uppercase">
                {t("adminContacts.dateReceived", "Date Received")}
              </p>
              <p className="text-xs text-gray-700 mt-0.5">
                {moment(selectedInquiry.createdAt).format("DD MMM YYYY, HH:mm:ss")}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-gray-400 uppercase">
                {t("adminContacts.status", "Status")}
              </p>
              <div>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold mt-1 ${
                    selectedInquiry.isResolved
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-amber-100 text-amber-800"
                  }`}
                >
                  {selectedInquiry.isResolved ? "Resolved" : "Pending"}
                </span>
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs font-bold uppercase text-gray-500 tracking-wider mb-2">
              {t("adminContacts.messageContent", "Message Content:")}
            </p>
            <div className="bg-gray-50 p-3 sm:p-4 rounded-xl text-xs sm:text-sm text-gray-800 leading-relaxed whitespace-pre-wrap border border-gray-200">
              {selectedInquiry.description}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ContactListView;
